/**
 * videoFallbackApprovalBroker.js — Feb 2026 (Phase 2)
 *
 * Bridge between the async video-generation cascade in `aiService.js` and
 * the React <VideoFallbackApprovalDialog> mounted at the app root.
 *
 * Contract (per user's spec, answer 3a):
 *   • Before EACH fallback attempt, generateVideo calls `requestApproval`
 *     with details about the model that just failed and the model about
 *     to be tried.
 *   • If a handler is registered (i.e. the React dialog is mounted), it
 *     shows the approval modal and resolves with {approved, cancel}.
 *   • If NO handler is registered (e.g. programmatic use, tests), we
 *     default to auto-approve so existing flows keep working.
 *
 * Payload shape passed to the handler:
 *   {
 *     failedModel:      { key, tier?, error }   // model that just failed
 *     nextModel:        { key, tier?, credits?, name? } // proposed next attempt
 *     attemptNumber:    1-based position of the NEXT attempt in the chain
 *     totalAttempts:    total number of fallback candidates
 *     capabilityLabel:  human-readable capability (e.g. 'References to video')
 *   }
 *
 * Handler MUST return a Promise resolving to:
 *   { approved: boolean }
 * If approved=false the video-gen cascade aborts with a user-cancelled error.
 */

let _handler = null;

/**
 * Register the modal's approval handler. Only one handler at a time.
 * Called by <VideoFallbackApprovalDialog> on mount.
 */
export const setApprovalHandler = (fn) => {
    _handler = typeof fn === 'function' ? fn : null;
};

/**
 * Unregister the handler. Called by <VideoFallbackApprovalDialog> on unmount.
 */
export const clearApprovalHandler = () => {
    _handler = null;
};

/**
 * Request approval before trying a fallback video model. Returns a promise
 * that resolves to true (approved) or false (user cancelled).
 *
 * @param {object} payload — see JSDoc above
 * @returns {Promise<boolean>} true if user approved, false if they cancelled
 */
export const requestApproval = async (payload) => {
    if (!_handler) {
        // Backward-compatible default — no dialog registered → auto-approve.
        // Preserves prior silent-cascade behaviour for non-UI callers / tests.
        console.log('🎬 [fallback-approval] No handler registered → auto-approve', payload);
        return true;
    }
    try {
        const result = await _handler(payload);
        const approved = !!(result && result.approved === true);
        console.log(`🎬 [fallback-approval] user ${approved ? 'APPROVED' : 'CANCELLED'} fallback to ${payload?.nextModel?.key || 'unknown'}`);
        return approved;
    } catch (err) {
        console.warn('🎬 [fallback-approval] handler threw — treating as cancelled', err);
        return false;
    }
};
